/* tslint:disable */
/* eslint-disable */
/**
 * Internal Variable Definitions Administration API
 *  ## Introduction  Variable Definitions are centralized definitions of concrete variables which are typically present in multiple datasets. Variable Definitions support standardization of data and metadata and facilitate sharing and joining of data by clarifying when variables have an identical definition.  ## Maintenance of Variable Definitions This API allows for creation, maintenance and access of Variable Definitions.  ### Ownership Creation and maintenance of variables may only be performed by Statistics Norway employees representing a specific Dapla team, who are defined as the owners of a given Variable Definition. In order to create variables, the single team an owner currently represents must be specified in the bearer token. This is currently only supported from the Dapla Lab platform. All maintenance is to be performed by the owners, with no intervention from administrators.  ### Status All Variable Definitions have an associated status. The possible values for status are `DRAFT`, `PUBLISHED_INTERNAL` and `PUBLISHED_EXTERNAL`.  #### Draft When a Variable Definition is created it is assigned the status `DRAFT`. Under this status the Variable Definition is:  - Only visible to Statistics Norway employees. - Mutable (it may be changed directly without need for versioning). - Not suitable to refer to from other systems.  This status may be changed to `PUBLISHED_INTERNAL` or `PUBLISHED_EXTERNAL` with a direct update.  #### Published Internal Under this status the Variable Definition is:  - Only visible to Statistics Norway employees. - Immutable (all changes are versioned). - Suitable to refer to in internal systems for statistics production. - Not suitable to refer to for external use (for example in Statistikkbanken).  This status may be changed to `PUBLISHED_EXTERNAL` by creating a Patch version.  #### Published External Under this status the Variable Definition is:  - Visible to the general public. - Immutable (all changes are versioned). - Suitable to refer to from any system.  This status may not be changed as it would break immutability. If a Variable Definition is no longer relevant then its period of validity should be ended by specifying a `valid_until` date in a Patch version.  ### Immutability Variable Definitions are immutable. This means that any changes must be performed in a strict versioning system. Consumers can avoid being exposed to breaking changes by specifying a `date_of_validity` when they request a Variable Definition.  #### Patches Patches are for changes which do not affect the fundamental meaning of the Variable Definition.  #### Validity Periods Validity Periods are versions with a period defined by a `valid_from` date and optionally a `valid_until` date. If the fundamental meaning of a Variable Definition is to be changed, it should be done by creating a new Validity Period.  
 *
 * The version of the OpenAPI document: 0.1
 * Contact: metadata@ssb.no
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  CompleteView,
  CreateValidityPeriod,
  Problem,
} from '../models/index';
import {
    CompleteViewFromJSON,
    CompleteViewToJSON,
    CreateValidityPeriodFromJSON,
    CreateValidityPeriodToJSON,
    ProblemFromJSON,
    ProblemToJSON,
} from '../models/index';

export interface CreateValidityPeriodRequest {
    variableDefinitionId: string;
    createValidityPeriod?: CreateValidityPeriod;
}

export interface ListValidityPeriodsRequest {
    variableDefinitionId: string;
}

/**
 * ValidityPeriodsApi - interface
 * 
 * @export
 * @interface ValidityPeriodsApiInterface
 */
export interface ValidityPeriodsApiInterface {
    /**
     * Create a new validity period for a variable definition.
     * @summary Create a new validity period for a variable definition.
     * @param {string} variableDefinitionId Unique identifier for the variable definition.
     * @param {CreateValidityPeriod} [createValidityPeriod] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ValidityPeriodsApiInterface
     */
    createValidityPeriodRaw(requestParameters: CreateValidityPeriodRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<CompleteView>>;

    /**
     * Create a new validity period for a variable definition.
     * Create a new validity period for a variable definition.
     */
    createValidityPeriod(requestParameters: CreateValidityPeriodRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<CompleteView>;

    /**
     * List all validity periods.
     * @summary List all validity periods.
     * @param {string} variableDefinitionId Unique identifier for the variable definition.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ValidityPeriodsApiInterface
     */
    listValidityPeriodsRaw(requestParameters: ListValidityPeriodsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<CompleteView>>>;

    /**
     * List all validity periods.
     * List all validity periods.
     */
    listValidityPeriods(requestParameters: ListValidityPeriodsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<CompleteView>>;

}

/**
 * 
 */
export class ValidityPeriodsApi extends runtime.BaseAPI implements ValidityPeriodsApiInterface {

    /**
     * Create a new validity period for a variable definition.
     * Create a new validity period for a variable definition.
     */
    async createValidityPeriodRaw(requestParameters: CreateValidityPeriodRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<CompleteView>> {
        if (requestParameters['variableDefinitionId'] == null) {
            throw new runtime.RequiredError(
                'variableDefinitionId',
                'Required parameter "variableDefinitionId" was null or undefined when calling createValidityPeriod().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("labid_token", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("keycloak_token", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }

        let urlPath = `/variable-definitions/{variable-definition-id}/validity-periods`;
        urlPath = urlPath.replace(`{${"variable-definition-id"}}`, encodeURIComponent(String(requestParameters['variableDefinitionId'])));

        const response = await this.request({
            path: urlPath,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: CreateValidityPeriodToJSON(requestParameters['createValidityPeriod']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => CompleteViewFromJSON(jsonValue));
    }

    /**
     * Create a new validity period for a variable definition.
     * Create a new validity period for a variable definition.
     */
    async createValidityPeriod(requestParameters: CreateValidityPeriodRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<CompleteView> {
        const response = await this.createValidityPeriodRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * List all validity periods.
     * List all validity periods.
     */
    async listValidityPeriodsRaw(requestParameters: ListValidityPeriodsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<CompleteView>>> {
        if (requestParameters['variableDefinitionId'] == null) {
            throw new runtime.RequiredError(
                'variableDefinitionId',
                'Required parameter "variableDefinitionId" was null or undefined when calling listValidityPeriods().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};


        let urlPath = `/variable-definitions/{variable-definition-id}/validity-periods`;
        urlPath = urlPath.replace(`{${"variable-definition-id"}}`, encodeURIComponent(String(requestParameters['variableDefinitionId'])));

        const response = await this.request({
            path: urlPath,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CompleteViewFromJSON));
    }

    /**
     * List all validity periods.
     * List all validity periods.
     */
    async listValidityPeriods(requestParameters: ListValidityPeriodsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<CompleteView>> {
        const response = await this.listValidityPeriodsRaw(requestParameters, initOverrides);
        return await response.value();
    }

}
